import React, { useState, useImperativeHandle, forwardRef, useRef } from 'react'
import contractContainer from '../../services/contractContainer'
import StaigeModal from '../styles/Modal'
import i18n from '../../languages/i18n'

import { searchAndSortGetCallAs } from '../../services/serviceHelper'
import { Role } from '@soccerwatch/common'
import { postCameraStatus } from '../../api/api-camera'
import { getPlutoSettingByType, postPlutoSetting, VolumeSettings } from '../../api/api-pluto'

import './SoundControll.scss'

interface Channel {
  name: string
  volumePercent: number
  volumeDB: number
  muted: boolean
}

interface ControlItem {
  control: string
  channels: Channel[]
}

type AudioSettings = ControlItem[]

interface VolumeSettingData {
  controlName: string
  volumes: number[]
}

interface VolumeSetting {
  deviceId: string
  type: string
  data: VolumeSettingData
  RowKey: string
  SLCreated: string
  SLCreatedBy: string
  SLLastUpdated: string
  SLLastUpdatedBy: string
  SLNeedsVerification: string
  locked: boolean
}

type SoundControllProps = {
  cameraId: string
  soundSettings?: AudioSettings
}

export interface SoundControllRef {
  handleOpenSoundControllModal: () => void
}

const sliderPoints = [0, 0, 1, 3, 5, 8, 13, 23, 29, 38, 48, 60, 65, 68, 71, 73, 79, 82, 86, 89, 96, 100]
const soundPoints = [0, 10, 20, 30, 40, 50, 60, 70, 75, 80, 85, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100]

const interpolate = (x: number, x0: number, y0: number, x1: number, y1: number): number => {
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0)
}

const mapUiToActual = (uiValue: number): number => {
  const clamped = Math.max(0, Math.min(100, uiValue))
  if (clamped <= sliderPoints[0]) return soundPoints[0]
  if (clamped >= sliderPoints[sliderPoints.length - 1]) return soundPoints[soundPoints.length - 1]

  let i = 1
  while (i < sliderPoints.length && sliderPoints[i] < clamped) {
    i++
  }

  return Math.round(
    interpolate(clamped, sliderPoints[i - 1], soundPoints[i - 1], sliderPoints[i], soundPoints[i])
  )
}

const mapActualToUi = (actualValue: number): number => {
  const clamped = Math.max(0, Math.min(100, actualValue))
  if (clamped <= soundPoints[0]) return sliderPoints[0]
  if (clamped >= soundPoints[soundPoints.length - 1]) return sliderPoints[sliderPoints.length - 1]

  let i = 1
  while (i < soundPoints.length && soundPoints[i] < clamped) {
    i++
  }

  return Math.round(
    interpolate(clamped, soundPoints[i - 1], sliderPoints[i - 1], soundPoints[i], sliderPoints[i])
  )
}

const SoundControll = forwardRef<SoundControllRef, SoundControllProps>((props, ref) => {
  const [cameraStatus, setCameraStatus] = useState<AudioSettings | null>(null)
  const [cameraVolumeSetting, setCameraVolumeSetting] = useState<VolumeSetting[]>([])
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const refModal = useRef<any>(null)

  const mapSettingsForUi = (settings: AudioSettings): AudioSettings => {
    return settings.map((control) => ({
      ...control,
      channels: control.channels.map((channel) => ({
        ...channel,
        volumePercent: mapActualToUi(channel.volumePercent)
      }))
    }))
  }

  const fetchCameraStatus = async () => {
    if (!props.cameraId) return
    setIsLoadingStatus(true)
    try {
      const volumeSettingsRaw: VolumeSettings[] = await getPlutoSettingByType(props.cameraId, 'volume')
      const volumeSettingsParsed = volumeSettingsRaw.map((item) => ({
        ...item,
        data: JSON.parse(item.data as string) as VolumeSettingData
      }))
      setCameraVolumeSetting(volumeSettingsParsed)
      if (props.soundSettings) {
        setCameraStatus(mapSettingsForUi(props.soundSettings))
        setIsLoadingStatus(false)
        return
      }

      const callAs = searchAndSortGetCallAs([
        Role.admin,
        contractContainer.getHighestRelevantRoleForCurrentContract()
      ])
      const response: any = await postCameraStatus(String(props.cameraId), callAs)
      if (response.detail.sound) {
        setCameraStatus(mapSettingsForUi(response.detail.sound))
      }
    } catch (err: any) {
      console.error('Error fetching camera status:', err)
      setCameraStatus(null)
    } finally {
      setIsLoadingStatus(false)
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpenSoundControllModal: async () => {
      await fetchCameraStatus()
      if (refModal.current) {
        refModal.current.handleOpenModal()
      }
    }
  }))

  const handleVolumeChange = (controlIndex: number, channelIndex: number, newUiVolume: number) => {
    let changedControlName = ''
    let newVolumesForControl: number[] = []

    setCameraStatus((currentStatus) => {
      if (!currentStatus) return null

      const newUiState = currentStatus.map((control, cIdx) => {
        if (cIdx !== controlIndex) return control

        const updatedChannels = control.channels.map((channel, chIdx) => {
          if (chIdx !== channelIndex) return channel
          return { ...channel, volumePercent: newUiVolume }
        })

        changedControlName = control.control
        newVolumesForControl = updatedChannels.map((ch) => ch.volumePercent)

        return { ...control, channels: updatedChannels }
      })
      return newUiState
    })

    setCameraVolumeSetting((currentSettings) => {
      const mappedActualVolumes = newVolumesForControl.map(mapUiToActual)

      const settingIndex = currentSettings.findIndex((s) => s.data.controlName === changedControlName)

      if (settingIndex !== -1) {
        return currentSettings.map((setting, index) => {
          if (index !== settingIndex) return setting
          return {
            ...setting,
            data: {
              ...setting.data,
              volumes: mappedActualVolumes
            }
          }
        })
      } else {
        const newSetting: any = {
          deviceId: 'swcs-' + props.cameraId,
          type: 'volume',
          data: { controlName: changedControlName, volumes: mappedActualVolumes }
        }
        return [...currentSettings, newSetting]
      }
    })
  }

  const handleSaveChanges = async () => {
    if (!cameraVolumeSetting) return
    setIsSaving(true)
    try {
      const settingsToUpdate = cameraVolumeSetting.map((s) => ({
        ...s,
        data: JSON.stringify(s.data),
        locked: true
      }))
      if (settingsToUpdate.length > 0) {
        settingsToUpdate.forEach(async (setting) => {
          if (setting.RowKey) {
            // @ts-ignore
            setting = {
              RowKey: setting.RowKey,
              type: setting.type,
              data: setting.data
            }
          }
          await postPlutoSetting(setting)
        })
      }

      if (refModal.current) {
        refModal.current.handleCloseModal()
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Sound-Einstellungen:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const renderSoundControls = () => {
    if (isLoadingStatus) return <p>{i18n.t('general.loading')}</p>
    if (!cameraStatus || cameraStatus.length === 0) return <p>{i18n.t('camera.sound.noData')}</p>

    const filteredControls = cameraStatus

    return (
      <div className='sound-control-list'>
        {filteredControls.map((item, controlIndex) => {
          if (!item.channels || item.channels.length === 0) return null

          const limitedChannels = item.channels.slice(0, 4)

          return (
            <div key={item.control} className='control-group'>
              <h3>{item.control}</h3>
              <div className='channel-list'>
                {limitedChannels.map((channel, channelIndex) => (
                  <div key={`${item.control}-${channelIndex}`} className='channel-item'>
                    <span className='volume-value'>{channel.volumePercent}%</span>
                    <input
                      type='range'
                      id={`volume-${controlIndex}-${channelIndex}`}
                      min='0'
                      max='100'
                      value={channel.volumePercent}
                      onChange={(e) =>
                        handleVolumeChange(controlIndex, channelIndex, parseInt(e.target.value, 10))
                      }
                      className='volume-slider'
                      disabled={isSaving}
                    />
                    <label htmlFor={`volume-${controlIndex}-${channelIndex}`}>Input {channelIndex + 1}</label>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <StaigeModal
      ref={refModal}
      title={i18n.t('Sound Control')}
      confirmText={isSaving ? i18n.t('general.saving') : i18n.t('general.confirm')}
      onConfirm={handleSaveChanges}
      disabledConfirm={isSaving || isLoadingStatus}
    >
      {renderSoundControls()}
    </StaigeModal>
  )
})

SoundControll.displayName = 'SoundControll'

export default SoundControll
